import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, Plus, Trophy, XCircle } from 'lucide-react';
import {
  ApiError,
  api,
  canEdit,
  type AuthUser,
  type VsKind,
  type VsOpponentSuggestion,
  type VsOutcome,
  type VsWeekRow,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { EventsTabs } from './Events';

function mondayOfCurrentWeek(): string {
  const d = new Date();
  const dow = d.getDay();
  const diff = (dow + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

const KIND_LABEL: Record<VsKind, string> = {
  save: 'Save Week',
  soft_push: 'Soft Push',
  push: 'Push Week',
  aborted_push: 'Aborted Push',
};

const KIND_SHORT: Record<VsKind, string> = {
  save: 'Save',
  soft_push: 'Soft',
  push: 'Push',
  aborted_push: 'Abort',
};

export default function EventsVs({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const listQuery = useQuery({ queryKey: ["vs"], queryFn: api.listVs });
  const importSummaryQuery = useQuery({
    queryKey: ['vs-import-summary'],
    queryFn: api.vsImportJobsSummary,
    refetchInterval: 5000,
  });

  if (!canEdit(user.role)) {
    return (
      <div className="space-y-4">
        <EventsTabs user={user} />
        <div className="text-slate-400">Only R4+ can view VS.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <EventsTabs user={user} />
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">VS matchups</h1>
          <p className="text-sm text-slate-400">
            Weekly Server-vs-Server entries. Sunday scores in millions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/events/vs/overview"
            className="inline-flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            <BarChart3 size={14} /> Overview
          </Link>
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
          >
            <Plus size={14} /> New VS
          </button>
        </div>
      </div>

      {creating && <CreateVsForm onClose={() => setCreating(false)} onCreated={(id) => navigate(`/events/vs/${id}`)} />}

      <section className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-3 py-2">Week</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Result</th>
              <th className="px-3 py-2">Opponent</th>
              <th className="px-3 py-2">Server</th>
              <th className="px-3 py-2">Top scorer</th>
              <th className="px-3 py-2 text-right">Players scored</th>
              <th className="px-3 py-2 text-right">Total (M)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {listQuery.isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {listQuery.data?.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-slate-500">
                  No VS entries yet.
                </td>
              </tr>
            )}
            {listQuery.data?.map((row) => (
              <Row
                key={row.id}
                row={row}
                importSummary={importSummaryQuery.data?.byWeek[row.id]}
                onOpen={() => navigate(`/events/vs/${row.id}`)}
              />
            ))}
          </tbody>
        </table>
      </section>
      <Style />
    </div>
  );
}

function Row({
  row,
  onOpen,
  importSummary,
}: {
  row: VsWeekRow;
  onOpen: () => void;
  importSummary?: { pending: number; ready: number; failed: number };
}) {
  const top = row.topScorer;
  const topLabel = top ? top.displayName?.trim() || top.name : null;
  return (
    <tr
      className="cursor-pointer hover:bg-slate-900/50"
      onClick={onOpen}
    >
      <td className="px-3 py-2 font-medium">
        <div className="flex items-center gap-2">
          <span>{row.weekStart}</span>
          {importSummary && importSummary.ready > 0 && (
            <span
              className="rounded bg-emerald-800/60 px-1.5 py-0.5 text-[10px] text-emerald-100"
              title="Video import ready for preview"
            >
              📼 ready
            </span>
          )}
          {importSummary && importSummary.pending > 0 && (
            <span
              className="rounded bg-indigo-800/60 px-1.5 py-0.5 text-[10px] text-indigo-100"
              title="Video import running"
            >
              📼 processing
            </span>
          )}
          {importSummary && importSummary.failed > 0 && (
            <span
              className="rounded bg-red-800/60 px-1.5 py-0.5 text-[10px] text-red-100"
              title="Video import failed"
            >
              📼 failed
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <KindPill kind={row.kind} />
      </td>
      <td className="px-3 py-2">
        {row.outcome ? <OutcomePillSmall outcome={row.outcome} /> : <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-2">{row.opponentName}</td>
      <td className="px-3 py-2 text-slate-300">{row.opponentServer}</td>
      <td className="px-3 py-2 text-slate-200">
        {top ? (
          <span>
            {topLabel}{' '}
            <span className="text-xs text-slate-500 tabular-nums">
              {top.scoreM.toLocaleString()} M
            </span>
          </span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{row.playerCount}</td>
      <td className="px-3 py-2 text-right tabular-nums">{row.totalScoreM.toLocaleString()}</td>
    </tr>
  );
}

function OutcomePillSmall({ outcome }: { outcome: VsOutcome }) {
  const isWin = outcome === 'win';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
        isWin ? 'bg-emerald-900/50 text-emerald-200' : 'bg-red-900/50 text-red-200',
      )}
    >
      {isWin ? <Trophy size={11} /> : <XCircle size={11} />}
      {isWin ? 'Win' : 'Loss'}
    </span>
  );
}

export function KindPill({ kind }: { kind: VsKind }) {
  return (
    <span
      className={cn(
        'inline-flex rounded px-2 py-0.5 text-xs font-medium',
        kind === 'push'
          ? 'bg-indigo-700/60 text-indigo-100'
          : kind === 'aborted_push'
            ? 'bg-rose-700/50 text-rose-100'
            : kind === 'soft_push'
              ? 'bg-amber-700/60 text-amber-100'
              : 'bg-slate-700/70 text-slate-200',
      )}
    >
      {KIND_LABEL[kind]}
    </span>
  );
}

function CreateVsForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [weekStart, setWeekStart] = useState(mondayOfCurrentWeek());
  const [kind, setKind] = useState<VsKind>('push');
  const [opponentName, setOpponentName] = useState('');
  const [opponentServer, setOpponentServer] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const suggestQuery = useQuery({
    queryKey: ['vs-suggest'],
    queryFn: api.suggestVsOpponents,
  });
  const suggestions = suggestQuery.data?.opponents ?? [];

  const nameSuggestions = useMemo(() => {
    const seen = new Set<string>();
    return suggestions.filter((s) => {
      if (seen.has(s.name)) return false;
      seen.add(s.name);
      return true;
    });
  }, [suggestions]);

  const pickSuggestion = (s: VsOpponentSuggestion) => {
    setOpponentName(s.name);
    setOpponentServer(s.server);
  };

  const create = useMutation({
    mutationFn: () =>
      api.createVs({
        weekStart,
        kind,
        opponentName: opponentName.trim(),
        opponentServer: opponentServer.trim(),
        notes: notes.trim() || null,
      }),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["vs"] });
      qc.invalidateQueries({ queryKey: ['vs-current'] });
      onCreated(row.id);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium uppercase tracking-wider text-slate-400">
          New VS
        </h3>
        <button
          onClick={onClose}
          className="rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!opponentName.trim() || !opponentServer.trim()) {
            setError('Opponent and server are required.');
            return;
          }
          create.mutate();
        }}
        className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,150px)_auto_minmax(0,1fr)_minmax(0,140px)_auto]"
      >
        <label className="block text-xs text-slate-400">
          Week start
          <input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            className="input mt-1"
            required
          />
        </label>
        <label className="block text-xs text-slate-400">
          Kind
          <div className="mt-1 inline-flex rounded-lg border border-slate-700 bg-slate-950 p-0.5 text-sm">
            {(['save', 'soft_push', 'push', 'aborted_push'] as const).map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => setKind(k)}
                className={cn(
                  'rounded px-3 py-1 transition',
                  kind === k ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white',
                )}
              >
                {KIND_SHORT[k]}
              </button>
            ))}
          </div>
        </label>
        <label className="block text-xs text-slate-400">
          Opponent alliance
          <input
            list="vs-opponent-names"
            className="input mt-1"
            value={opponentName}
            onChange={(e) => {
              const next = e.target.value;
              setOpponentName(next);
              const hit = suggestions.find(
                (s) => s.name.toLowerCase() === next.toLowerCase(),
              );
              if (hit && !opponentServer) setOpponentServer(hit.server);
            }}
            placeholder="Alliance name"
            required
          />
          <datalist id="vs-opponent-names">
            {nameSuggestions.map((s) => (
              <option key={s.name} value={s.name} />
            ))}
          </datalist>
        </label>
        <label className="block text-xs text-slate-400">
          Origin server
          <input
            list="vs-opponent-servers"
            className="input mt-1"
            value={opponentServer}
            onChange={(e) => setOpponentServer(e.target.value)}
            placeholder="#1234"
            required
          />
          <datalist id="vs-opponent-servers">
            {suggestions.map((s) => (
              <option key={`${s.name}::${s.server}`} value={s.server}>
                {s.name}
              </option>
            ))}
          </datalist>
        </label>
        <button className="btn-primary self-end" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <label className="col-span-full block text-xs text-slate-400">
          Notes (optional)
          <input
            className="input mt-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
      </form>
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1 text-xs">
          <span className="text-slate-500">Recent:</span>
          {suggestions.slice(0, 6).map((s) => (
            <button
              type="button"
              key={`${s.name}::${s.server}`}
              onClick={() => pickSuggestion(s)}
              className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800"
            >
              {s.name} <span className="text-slate-500">{s.server}</span>
            </button>
          ))}
        </div>
      )}
      {error && <div className="text-sm text-red-400">{error}</div>}
    </section>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
